import { Flex, Group, Tabs, Title } from "@mantine/core";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";

import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import ProposalPromptsTab from "@/components/features/ProposalPrompts/ProposalPromptsTab";
import { getActiveSessionState } from "@/hooks/useActiveSession";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { usePersistedTabs } from "@/hooks/usePersistedTabs";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { canManageProposalPrompts } from "@/utils/proposals/canManageProposalPrompts";

import type { TabItemType } from "@/types/common.types";

import styles from "./proposals.module.scss";

const manageSearchSchema = z.object({
  tab: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_authenticated/proposals/manage")({
  component: ProposalsManage,
  validateSearch: manageSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });

    // SERV-3053: the only tab is the AI prompts, which the backend refuses to
    // anyone but super admins and Proposal Specialists.
    const session = getActiveSessionState();
    if (!canManageProposalPrompts(session.userRoles, session.userType)) {
      throw redirect({ to: "/proposals" });
    }
  },
});

const DEFAULT_TAB = "ai-prompts";

const MANAGE_TABS: TabItemType[] = [
  { value: DEFAULT_TAB, label: "AI Prompts", component: <ProposalPromptsTab /> },
];

function ProposalsManage() {
  useBreadcrumb([
    {
      title: "Proposals",
      href: "/proposals",
      icon: getRouteIcon(MODULE_KEYS_MAP.proposals),
    },
    { title: "Manage", href: "/proposals/manage" },
  ]);

  const { activeTab, setActiveTab } = usePersistedTabs({ defaultTab: DEFAULT_TAB });

  return (
    <Flex direction="column" className={styles.container}>
      <Group justify="space-between" align="center" className={styles.header}>
        <Title order={3} className={styles.title}>
          Manage Proposals
        </Title>
      </Group>

      <Tabs
        value={activeTab}
        onChange={(value) => setActiveTab(value || DEFAULT_TAB)}
        keepMounted={false}
        className={styles.tab}
      >
        <Tabs.List className={styles.tabsList}>
          {MANAGE_TABS.map((tab) => (
            <Tabs.Tab key={tab.value} value={tab.value} className={styles.tab}>
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {MANAGE_TABS.map((tab) => (
          <Tabs.Panel key={tab.value} value={tab.value} className={styles.tabPanel}>
            {tab.component}
          </Tabs.Panel>
        ))}
      </Tabs>
    </Flex>
  );
}
